import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useImageLibrary, uploadThumbnail, deleteThumbnail } from "@/hooks/useImageLibrary";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { ImagePlus, Upload, X, Check, Loader2, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface ImagePickerProps {
  value?: string | null;
  onChange: (url: string | null) => void;
  label?: string;
}

export function ImagePicker({ value, onChange, label = "Featured Image" }: ImagePickerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(value || null);
  const [deleteTarget, setDeleteTarget] = useState<{ path: string; url: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: images, isLoading: imagesLoading } = useImageLibrary();

  const deleteImageMutation = useMutation({
    mutationFn: async (path: string) => {
      await deleteThumbnail(path);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["image-library"] });
      toast({ title: "Image deleted" });
      // Clear selection if the deleted image was selected
      if (deleteTarget && selectedImage === deleteTarget.url) {
        setSelectedImage(null);
      }
      setDeleteTarget(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete image.",
        variant: "destructive",
      });
      setDeleteTarget(null);
    },
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file (JPG, PNG, GIF, WebP).",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Image must be less than 5MB.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploading(true);
      const url = await uploadThumbnail(file);
      setSelectedImage(url);
      queryClient.invalidateQueries({ queryKey: ["image-library"] });
      toast({ title: "Image uploaded!" });
    } catch (error: any) {
      console.error("Upload error:", error);
      toast({
        title: "Upload failed",
        description: error?.message || "Failed to upload image.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleConfirm = () => {
    onChange(selectedImage);
    setOpen(false);
  };

  const handleRemove = () => {
    onChange(null);
    setSelectedImage(null);
  };

  const handleDeleteClick = (e: React.MouseEvent, image: { path: string; url: string }) => {
    e.stopPropagation();
    setDeleteTarget(image);
  };

  const handleConfirmDelete = () => {
    if (deleteTarget) {
      deleteImageMutation.mutate(deleteTarget.path);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-start gap-3">
        {value ? (
          <div className="relative group">
            <img
              src={value}
              alt="Featured"
              className="w-32 h-20 object-cover rounded-md border"
            />
            <Button
              variant="destructive"
              size="icon"
              className="absolute -top-2 -right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={handleRemove}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="w-32 h-20 border-2 border-dashed rounded-md flex items-center justify-center bg-muted/50">
            <ImagePlus className="h-6 w-6 text-muted-foreground" />
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              {value ? "Change" : "Select Image"}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Select Featured Image</DialogTitle>
            </DialogHeader>

            <Tabs defaultValue="library" className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="library">Image Library</TabsTrigger>
                <TabsTrigger value="upload">Upload New</TabsTrigger>
              </TabsList>

              <TabsContent value="library" className="mt-4">
                {imagesLoading ? (
                  <div className="grid grid-cols-4 gap-3">
                    {[...Array(8)].map((_, i) => (
                      <Skeleton key={i} className="aspect-video rounded-md" />
                    ))}
                  </div>
                ) : images && images.length > 0 ? (
                  <ScrollArea className="h-64">
                    <div className="grid grid-cols-4 gap-3 pr-4">
                      {images.map((image) => (
                        <div
                          key={image.name}
                          className="relative group"
                        >
                          <button
                            onClick={() => setSelectedImage(image.url)}
                            className={cn(
                              "relative aspect-video rounded-md overflow-hidden border-2 transition-all w-full",
                              selectedImage === image.url
                                ? "border-primary ring-2 ring-primary/20"
                                : "border-transparent hover:border-muted-foreground/30"
                            )}
                          >
                            <img
                              src={image.url}
                              alt={image.name}
                              className="w-full h-full object-cover"
                            />
                            {selectedImage === image.url && (
                              <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                <Check className="h-6 w-6 text-primary" />
                              </div>
                            )}
                          </button>
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                            onClick={(e) => handleDeleteClick(e, { path: image.path, url: image.url })}
                            disabled={deleteImageMutation.isPending}
                          >
                            {deleteImageMutation.isPending && deleteTarget?.path === image.path ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="py-12 text-center text-muted-foreground">
                    <ImagePlus className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No images in library yet.</p>
                    <p className="text-sm">Upload an image to get started.</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="upload" className="mt-4">
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={isUploading}
                  />
                  <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-3">
                    Drag and drop an image, or click to select
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      "Select Image"
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-3">
                    JPG, PNG, GIF, WebP up to 5MB
                  </p>
                </div>

                {selectedImage && (
                  <div className="mt-4 p-3 border rounded-md">
                    <p className="text-sm font-medium mb-2">Selected image:</p>
                    <img
                      src={selectedImage}
                      alt="Selected"
                      className="w-full max-h-40 object-contain rounded"
                    />
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={!selectedImage}>
                Use This Image
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Image?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The image will be permanently deleted and may affect content using it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
